import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import {
  CtyButton,
  getTableState,
  useAgGridStatePersistence,
} from '@circuitry-ai/doc-ui';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  WORK_QUEUE_ROUTING_METHOD_LABELS,
  type WorkQueue,
  type WorkQueueEntityType,
} from './work-queues-types';

const WORK_QUEUES_GRID_ID = 'work-queues-grid';

type QueuesGridContext = {
  entityTypesByUuid: Record<string, WorkQueueEntityType>;
  onEdit: (queue: WorkQueue) => void;
  onDelete: (queue: WorkQueue) => void;
};

const CENTERED_CELL_CLASS = 'flex items-center';

function QueueNameCellRenderer(params: ICellRendererParams<WorkQueue>) {
  const queue = params.data;
  if (!queue) {
    return null;
  }
  return (
    <div className="flex flex-col justify-center py-2 leading-5">
      <span className="text-sm font-medium text-foreground">{queue.name}</span>
      {queue.description && (
        <span className="text-xs text-default-500">{queue.description}</span>
      )}
    </div>
  );
}

function EntityTypeCellRenderer(params: ICellRendererParams<WorkQueue>) {
  const queue = params.data;
  const ctx = params.context as QueuesGridContext | undefined;
  if (!queue) {
    return null;
  }
  const entityType = ctx?.entityTypesByUuid[queue.entity_type];
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm">
      {entityType && (
        <Icon
          icon={entityType.icon}
          className="text-primary shrink-0"
          width={16}
          height={16}
        />
      )}
      {entityType?.name ?? (
        // Definition id not in the static entity type list
        <span
          className="text-default-500"
          title={queue.entity_type || undefined}
        >
          {queue.entity_type ? 'Unknown' : '—'}
        </span>
      )}
    </span>
  );
}

function TeamRoleCellRenderer(params: ICellRendererParams<WorkQueue>) {
  if (!params.data?.team_role) {
    return params.data ? <span className="text-default-400">—</span> : null;
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-default-100 text-default-700">
      {params.data.team_role}
    </span>
  );
}

function StatusCellRenderer(params: ICellRendererParams<WorkQueue>) {
  if (!params.data) {
    return null;
  }
  const isActive = params.data.is_active;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? 'bg-success-50 text-success-600'
          : 'bg-default-100 text-default-500'
      }`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {isActive ? 'Active' : 'Paused'}
    </span>
  );
}

function ActionsCellRenderer(params: ICellRendererParams<WorkQueue>) {
  const queue = params.data;
  const ctx = params.context as QueuesGridContext | undefined;
  if (!queue || !ctx) {
    return null;
  }
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => ctx.onEdit(queue)}
        className="p-2 hover:bg-default-100 rounded-full"
        aria-label={`Edit ${queue.name}`}
      >
        <Icon icon="solar:pen-linear" className="w-4 h-4 text-default-600" />
      </button>
      <button
        type="button"
        onClick={() => ctx.onDelete(queue)}
        className="p-2 hover:bg-danger-50 rounded-full"
        aria-label={`Delete ${queue.name}`}
      >
        <Icon icon="hugeicons:delete-02" className="w-4 h-4 text-danger" />
      </button>
    </div>
  );
}

const QUEUES_COLUMN_DEFS: ColDef<WorkQueue>[] = [
  {
    headerName: 'Queue',
    field: 'name',
    flex: 2,
    minWidth: 220,
    autoHeight: true,
    cellRenderer: QueueNameCellRenderer,
  },
  {
    headerName: 'Entity Type',
    colId: 'entityType',
    flex: 1.3,
    minWidth: 160,
    cellRenderer: EntityTypeCellRenderer,
  },
  {
    headerName: 'Team Role',
    field: 'team_role',
    flex: 1.1,
    minWidth: 140,
    cellRenderer: TeamRoleCellRenderer,
  },
  {
    headerName: 'Members',
    colId: 'members',
    flex: 0.8,
    minWidth: 110,
    // Dash when the member records couldn't be loaded (a real 0 still shows as 0)
    valueGetter: (params) => params.data?.member_count ?? '—',
  },
  {
    headerName: 'Routing',
    field: 'routing_method',
    flex: 1.1,
    minWidth: 140,
    valueFormatter: (params) =>
      WORK_QUEUE_ROUTING_METHOD_LABELS[
        params.value as WorkQueue['routing_method']
      ] ?? params.value,
  },
  {
    headerName: 'SLA',
    field: 'sla_hours',
    flex: 0.6,
    minWidth: 90,
    valueFormatter: (params) => (params.value ? `${params.value} hrs` : '—'),
  },
  {
    headerName: 'Status',
    field: 'is_active',
    flex: 0.8,
    minWidth: 110,
    cellRenderer: StatusCellRenderer,
  },
  {
    headerName: 'Actions',
    colId: 'actions',
    minWidth: 120,
    maxWidth: 120,
    pinned: 'right',
    lockPinned: true,
    lockPosition: true,
    resizable: false,
    sortable: false,
    cellRenderer: ActionsCellRenderer,
  },
];

export function QueuesTab({
  queues,
  entityTypes,
  onCreate,
  onEdit,
  onDelete,
  isLoading = false,
}: Readonly<{
  queues: WorkQueue[];
  isLoading?: boolean;
  entityTypes: WorkQueueEntityType[];
  onCreate: () => void;
  onEdit: (queue: WorkQueue) => void;
  onDelete: (queue: WorkQueue) => void;
}>) {
  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
    useAgGridStatePersistence(WORK_QUEUES_GRID_ID);
  const columnDefs = useMemo(
    () => mergeColumnDefs(QUEUES_COLUMN_DEFS),
    [mergeColumnDefs]
  );

  const gridContext = useMemo<QueuesGridContext>(
    () => ({
      entityTypesByUuid: Object.fromEntries(
        entityTypes.map((entityType) => [entityType.uuid, entityType])
      ),
      onEdit,
      onDelete,
    }),
    [entityTypes, onEdit, onDelete]
  );

  const gridApiRef = useRef<GridApi<WorkQueue> | null>(null);

  // ag-grid v31 has no `loading` prop, so drive the overlay through the API
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    if (isLoading) {
      api.showLoadingOverlay();
    } else if (queues.length) {
      api.hideOverlay();
    } else {
      api.showNoRowsOverlay();
    }
  }, [isLoading, queues.length]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<WorkQueue>) => {
      gridApiRef.current = params.api;
      if (isLoading) {
        params.api.showLoadingOverlay();
      }
      augmentGridReady(params, (p) => {
        if (!getTableState(WORK_QUEUES_GRID_ID)?.columnState?.length) {
          p?.api?.sizeColumnsToFit?.();
        }
      });
    },
    // isLoading is only read for the initial overlay; later changes go through the effect above
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [augmentGridReady]
  );

  return (
    <div className="rounded-xl border border-default-200 bg-content1 p-6">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">Queues</h2>
          <p className="text-sm text-default-500">
            Each queue pulls one entity type and assigns it to members of a role
          </p>
        </div>
        <CtyButton
          size="md"
          color="primary"
          variant="solid"
          onPress={onCreate}
          startContent={<Icon icon="mdi:plus" width={16} height={16} />}
        >
          Create Queue
        </CtyButton>
      </div>

      <div
        className="ag-theme-alpine w-full"
        style={
          {
            '--ag-border-color': '#e0e0e0',
            '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
            '--ag-wrapper-border-radius': '6px',
          } as React.CSSProperties
        }
      >
        <AgGridReact<WorkQueue>
          rowData={queues}
          columnDefs={columnDefs}
          context={gridContext}
          getRowId={(params) => params.data.queue_id}
          defaultColDef={{
            resizable: true,
            suppressMovable: true,
            cellClass: CENTERED_CELL_CLASS,
          }}
          headerHeight={44}
          rowHeight={60}
          domLayout="autoHeight"
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          overlayNoRowsTemplate='<span class="text-default-500">No queues yet.</span>'
          overlayLoadingTemplate='<span class="text-default-500">Loading queues...</span>'
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
}

export default QueuesTab;
